import { EntityType, useAppSelector } from '@frontend/common';
import { DocumentClient } from '@frontend/document/api';
import { Document } from '@frontend/document/types';
import { useLocale } from '@frontend/lang';
import { useModals } from '@frontend/modal';
import { useAccountRepository } from '@frontend/repository';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { fetchProfile, userStore } from '@frontend/user/redux';
import { IUser } from '@frontend/user/types';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../redux/store';
import { useIsMount } from '../utils/hooks';

interface ViewProps {
    user: IUser | undefined;
    openModal: ReturnType<typeof useModals>['openModal'];
    closeModal: ReturnType<typeof useModals>['closeModal'];
    userLanguageModalId: string;
    activeTab: string;
    setActiveTab: (arg: string) => void;
    privacyLoading: boolean;
    privacyDocumentFile?: { blob: Blob; url: string } | null;
}

const useProfile = (): ViewProps => {
    const { locale } = useLocale();
    const { currentAccount } = useAccountRepository();
    const dispatch = useAppDispatch();
    const [user, changeUser] = useState<IUser | undefined>(undefined);
    const userState = useAppSelector(useSelector, userStore);
    const { openModal, closeModal } = useModals();
    const userLanguageModalId = 'user-language-modal';
    const [activeTab, setActiveTab] = useState<string>('general');
    const [privacyLoading, setPrivacyLoading] = useState<boolean>(true);
    const [accountPrivacyDocument, setAccountPrivacyDocument] = useState<Document | null>(null);
    const [privacyDocumentFile, setPrivacyDocumentFile] = useState<{ blob: Blob; url: string } | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const isMount = useIsMount();

    useEffect(() => {
        dispatch(fetchProfile());
        SettingClient.fetchSettings({ entity_type: EntityType.ACCOUNT, entity_id: currentAccount })
            .then((settingsResult) => {
                const privacyDocSetting = settingsResult.results.find((s) => s.key === SettingKey.PRIVACY_POLICY_DOCUMENT_ID);
                if (privacyDocSetting && privacyDocSetting.value && typeof privacyDocSetting.value === 'string') {
                    DocumentClient.fetchAccountDocument(currentAccount, privacyDocSetting.value)
                        .then(setAccountPrivacyDocument)
                        .catch(() => setPrivacyLoading(false));
                } else setPrivacyLoading(false);
            })
            .catch(() => setPrivacyLoading(false));
    }, []);

    useEffect(() => {
        if (accountPrivacyDocument && accountPrivacyDocument.document_info) {
            DocumentClient.fetchDocumentFile(currentAccount, accountPrivacyDocument.id, locale)
                .then((file) => {
                    const url = URL.createObjectURL(file);
                    setPrivacyDocumentFile({ blob: file, url });
                    setPrivacyLoading(false);
                })
                .catch(() => setPrivacyLoading(false));
        }

        return () => {
            if (privacyDocumentFile) {
                URL.revokeObjectURL(privacyDocumentFile.url);
            }
        };
    }, [accountPrivacyDocument]);

    useEffect(() => {
        if (isMount) {
            return;
        } else {
            setSearchParams((prev) => {
                const params = new URLSearchParams();
                params.set('tab', activeTab);
                return params;
            });
        }
    }, [activeTab]);

    useEffect(() => {
        if (userState.current) changeUser(userState.current);
    }, [userState.current]);

    return {
        user,
        openModal,
        closeModal,
        userLanguageModalId,
        activeTab,
        setActiveTab,
        privacyLoading,
        privacyDocumentFile
    };
};

export default useProfile;
